import type { Country } from "react-phone-number-input";

import {
  AU_CALLING_CODE,
  AU_COUNTRY,
  AU_DOMESTIC_PREFIX,
  AU_MOBILE_PREFIX,
  NP_CALLING_CODE,
  NP_DOMESTIC_PREFIX,
} from "@/components/ui/phoneInput.constants";

const removePhoneInputSpacing = (phoneNumber: string): string => {
  return phoneNumber.replace(/[\s()-]/g, "");
};

const DEACTIVATED_EMPLOYEE_PHONE_PREFIX = "__DEACTIVATED_EMPLOYEE_PHONE__";

/**
 * Removes the backend-only deactivation marker from an employee phone number.
 * @param phoneNumber - Raw phone number value.
 * @returns Phone number without the deactivation marker.
 */
export const stripDeactivatedEmployeePhonePrefix = (
  phoneNumber: string | null | undefined,
): string | null | undefined => {
  if (!phoneNumber?.startsWith(DEACTIVATED_EMPLOYEE_PHONE_PREFIX)) {
    return phoneNumber;
  }

  const originalPhoneStart = phoneNumber.lastIndexOf(":");

  return originalPhoneStart === -1
    ? phoneNumber
    : phoneNumber.slice(originalPhoneStart + 1);
};

const formatAuMobileGroups = (phoneNumber: string): string => {
  return [
    phoneNumber.slice(0, 4),
    phoneNumber.slice(4, 7),
    phoneNumber.slice(7),
  ]
    .filter(Boolean)
    .join(" ");
};

const formatAuInternationalGroups = (phoneNumber: string): string => {
  const localPhoneNumber = phoneNumber.slice(AU_CALLING_CODE.length);

  return [
    AU_CALLING_CODE,
    localPhoneNumber.slice(0, 3),
    localPhoneNumber.slice(3, 6),
    localPhoneNumber.slice(6),
  ]
    .filter(Boolean)
    .join(" ");
};

const formatNpMobileGroups = (phoneNumber: string): string => {
  return [phoneNumber.slice(0, 3), phoneNumber.slice(3)]
    .filter(Boolean)
    .join(" ");
};

const formatNpInternationalGroups = (phoneNumber: string): string => {
  const localPhoneNumber = phoneNumber.slice(NP_CALLING_CODE.length);

  return [
    NP_CALLING_CODE,
    localPhoneNumber.slice(0, 3),
    localPhoneNumber.slice(3),
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * Checks whether the shared phone input should use the AU-only input path.
 *
 * @param defaultCountry - Selected default country.
 * @param allowedCountries - Countries allowed by the caller.
 * @param allowAllCountries - Whether unrestricted country selection is enabled.
 * @returns Whether the AU-only input should be used.
 */
export const isAuOnlyPhoneInput = (
  defaultCountry: Country | undefined,
  allowedCountries: Country[] | undefined,
  allowAllCountries: boolean,
): boolean => {
  const hasOnlyAuAllowed =
    !allowedCountries ||
    (allowedCountries.length === 1 && allowedCountries[0] === AU_COUNTRY);

  return (
    !allowAllCountries && defaultCountry === AU_COUNTRY && hasOnlyAuAllowed
  );
};

/**
 * Formats supported AU and NP phone numbers for display.
 *
 * @param phoneNumber - Raw phone number value.
 * @returns Formatted phone number for display.
 */
export const formatPhoneNumber = (phoneNumber: string | undefined): string => {
  if (!phoneNumber) {
    return "";
  }

  const compactPhoneNumber = removePhoneInputSpacing(
    stripDeactivatedEmployeePhonePrefix(phoneNumber) ?? "",
  );

  if (compactPhoneNumber.startsWith(AU_CALLING_CODE)) {
    return formatAuInternationalGroups(compactPhoneNumber);
  }

  if (compactPhoneNumber.startsWith(NP_CALLING_CODE)) {
    return formatNpInternationalGroups(compactPhoneNumber);
  }

  if (compactPhoneNumber.startsWith(AU_DOMESTIC_PREFIX)) {
    return formatAuMobileGroups(compactPhoneNumber);
  }

  if (compactPhoneNumber.startsWith(NP_DOMESTIC_PREFIX)) {
    return formatNpMobileGroups(compactPhoneNumber);
  }

  return compactPhoneNumber;
};

/**
 * Normalizes AU input while accepting either local mobile or international format.
 *
 * @param phoneInput - User-entered phone input.
 * @returns Normalized phone number value.
 */
export const normalizeAuFlexiblePhoneInput = (phoneInput: string): string => {
  const compactPhoneInput = removePhoneInputSpacing(phoneInput);

  if (compactPhoneInput.startsWith(AU_CALLING_CODE)) {
    return compactPhoneInput;
  }

  if (compactPhoneInput.startsWith(AU_MOBILE_PREFIX)) {
    return `${AU_CALLING_CODE}${compactPhoneInput}`;
  }

  return compactPhoneInput;
};

/**
 * Maps the current caret to the equivalent display position after normalization.
 *
 * @param phoneInput - User-entered phone input.
 * @param cursorPosition - Current caret position in the user-entered value.
 * @returns Equivalent caret position in the formatted value.
 */
export const getFormattedPhoneInputCursorPosition = (
  phoneInput: string,
  cursorPosition: number,
): number => {
  const compactInputBeforeCursor = removePhoneInputSpacing(
    phoneInput.slice(0, cursorPosition),
  );
  const normalizedInputBeforeCursor = normalizeAuFlexiblePhoneInput(
    compactInputBeforeCursor,
  );
  const normalizedPhoneInput = normalizeAuFlexiblePhoneInput(phoneInput);
  const formattedPhoneInput = formatPhoneNumber(normalizedPhoneInput);
  let compactCharacterCount = 0;

  if (!normalizedInputBeforeCursor) {
    return 0;
  }

  for (let index = 0; index < formattedPhoneInput.length; index += 1) {
    if (/\s/.test(formattedPhoneInput[index])) {
      continue;
    }

    compactCharacterCount += 1;

    if (compactCharacterCount >= normalizedInputBeforeCursor.length) {
      return index + 1;
    }
  }

  return formattedPhoneInput.length;
};
